<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="handleClose"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <!-- Header -->
            <div class="sticky top-0 bg-white dark:bg-black border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <div class="flex items-center">
                <div class="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mr-3">
                  <svg class="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                  Add New Vehicle
                </h3>
              </div>
              <button
                @click="handleClose"
                class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="handleSubmit" class="px-6 py-4">
              <!-- Vehicle Information -->
              <div class="mb-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Vehicle Information</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Vehicle Make -->
                  <div>
                    <label for="vehicle_make" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Vehicle Make <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="vehicle_make"
                      v-model="formData.vehicle_make"
                      type="text"
                      placeholder="e.g., Toyota, Honda, Ford"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white"
                      :style="{ borderColor: brandColors.primary, '--tw-ring-color': brandColors.primary, 'outline': 'none' }"
                    />
                    <p v-if="errors.vehicle_make" class="mt-1 text-sm text-red-600 dark:text-red-400">
                      {{ errors.vehicle_make }}
                    </p>
                  </div>

                  <!-- Vehicle Model -->
                  <div>
                    <label for="vehicle_model" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Vehicle Model <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="vehicle_model"
                      v-model="formData.vehicle_model"
                      type="text"
                      placeholder="e.g., Camry, Civic, F-150"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white"
                      :style="{ borderColor: brandColors.primary, '--tw-ring-color': brandColors.primary, 'outline': 'none' }"
                    />
                    <p v-if="errors.vehicle_model" class="mt-1 text-sm text-red-600 dark:text-red-400">
                      {{ errors.vehicle_model }}
                    </p>
                  </div>

                  <!-- Vehicle Year -->
                  <div>
                    <label for="vehicle_year" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Vehicle Year <span class="text-red-500">*</span>
                    </label>
                    <select
                      id="vehicle_year"
                      v-model.number="formData.vehicle_year"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white"
                      :style="{ borderColor: brandColors.primary, '--tw-ring-color': brandColors.primary, 'outline': 'none' }"
                    >
                      <option value="" disabled>Select year</option>
                      <option v-for="year in yearOptions" :key="year" :value="year">
                        {{ year }}
                      </option>
                    </select>
                    <p v-if="errors.vehicle_year" class="mt-1 text-sm text-red-600 dark:text-red-400">
                      {{ errors.vehicle_year }}
                    </p>
                  </div>

                  <!-- Plate Number -->
                  <div>
                    <label for="plate_number" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Plate Number <span class="text-red-500">*</span>
                    </label>
                    <input
                      id="plate_number"
                      v-model="formData.plate_number"
                      type="text"
                      placeholder="e.g., ABC-1234"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white font-mono uppercase"
                      :style="{ borderColor: brandColors.primary, '--tw-ring-color': brandColors.primary, 'outline': 'none' }"
                    />
                    <p v-if="errors.plate_number" class="mt-1 text-sm text-red-600 dark:text-red-400">
                      {{ errors.plate_number }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Bulk Vehicle Photos Upload -->
              <!--
              <div class="mb-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Vehicle Photos (Optional)
                </h4>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Upload Vehicle Photos (Max 4)
                  </label>

                  <label
                    for="vehicle_photos"
                    @dragover.prevent="handleDragOver"
                    @dragleave.prevent="handleDragLeave"
                    @drop.prevent="handleDrop"
                    class="flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200"
                    :class="isDragging
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'"
                  >
                    <div class="space-y-1 text-center">
                      <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                      <div class="flex text-sm text-gray-600 dark:text-gray-400">
                        <span class="font-semibold text-blue-600 dark:text-blue-400">Click to upload</span>
                        <span class="ml-1">or drag and drop</span>
                      </div>
                      <p class="text-xs text-gray-500 dark:text-gray-400">
                        JPEG, PNG, JPG (MAX. 5MB per file, 4 photos max)
                      </p>
                    </div>
                    <input
                      id="vehicle_photos"
                      ref="fileInputRef"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      multiple
                      class="hidden"
                      @change="handleFileSelect"
                    />
                  </label>

                  <div v-if="vehiclePhotos.length > 0" class="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div
                      v-for="(photo, index) in vehiclePhotos"
                      :key="index"
                      class="relative group"
                    >
                      <img
                        :src="getPhotoPreview(photo)"
                        :alt="`Vehicle photo ${index + 1}`"
                        class="w-full h-24 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600"
                      />
                      <button
                        type="button"
                        @click="removePhoto(index)"
                        class="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      >
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <p v-if="errors.vehicle_photos" class="mt-2 text-sm text-red-600 dark:text-red-400">
                    {{ errors.vehicle_photos }}
                  </p>
                </div>
              </div>

              -->

              <!-- Vehicle Photos Upload -->
              <div class="mb-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Vehicle Photos <span class="text-red-500">*</span>
                </h4>

                <div class="grid grid-cols-2 sm:grid-cols-4 gap-6">

                  <!-- FRONT -->
                  <div class="text-center">
                    <label for="photo_front" class="cursor-pointer block">
                      <div class="border-2 border-dashed rounded-lg p-3 transition relative overflow-hidden"
                      :style="{ backgroundColor: brandColors.primary }">
                        <img
                          src="@/assets/Front.png"
                          alt="Front View"
                          class="w-full h-16 object-contain rounded"
                        />
                      </div>
                      <p class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        FRONT <span class="text-red-500">*</span>
                      </p>
                    </label>
                    <input
                      id="photo_front"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      class="hidden"
                      @change="handlePhotoSelect('front', $event)"
                    />
                    <p v-if="errors.photo_front" class="mt-1 text-xs text-red-600 dark:text-red-400">
                      {{ errors.photo_front }}
                    </p>
                  </div>

                  <!-- BACK -->
                  <div class="text-center">
                    <label for="photo_back" class="cursor-pointer block">
                      <div class="border-2 border-dashed rounded-lg p-3 transition relative overflow-hidden"
                      :style="{ backgroundColor: brandColors.primary }">
                        <img
                          src="@/assets/Back.png"
                          alt="Back View"
                          class="w-full h-16 object-contain rounded"
                        />
                      </div>
                      <p class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        BACK <span class="text-red-500">*</span>
                      </p>
                    </label>
                    <input
                      id="photo_back"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      class="hidden"
                      @change="handlePhotoSelect('back', $event)"
                    />
                    <p v-if="errors.photo_back" class="mt-1 text-xs text-red-600 dark:text-red-400">
                      {{ errors.photo_back }}
                    </p>
                  </div>

                  <!-- SIDE LEFT -->
                  <div class="text-center">
                    <label for="photo_left" class="cursor-pointer block">
                      <div class="border-2 border-dashed rounded-lg p-3 transition relative overflow-hidden"
                      :style="{ backgroundColor: brandColors.primary }">
                        <img
                          src="@/assets/Right.png"
                          alt="Left View"
                          class="w-full h-16 object-contain rounded"
                        />
                      </div>
                      <p class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        SIDE (L) <span class="text-red-500">*</span>
                      </p>
                    </label>
                    <input
                      id="photo_left"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      class="hidden"
                      @change="handlePhotoSelect('left', $event)"
                    />
                    <p v-if="errors.photo_left" class="mt-1 text-xs text-red-600 dark:text-red-400">
                      {{ errors.photo_left }}
                    </p>
                  </div>

                  <!-- SIDE RIGHT -->
                  <div class="text-center">
                    <label for="photo_right" class="cursor-pointer block">
                      <div class="border-2 border-dashed rounded-lg p-3 transition relative overflow-hidden"
                      :style="{ backgroundColor: brandColors.primary }">
                        <img
                          src="@/assets/Left.png"
                          alt="Right View"
                          class="w-full h-16 object-contain rounded"
                        />
                      </div>
                      <p class="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        SIDE (R) <span class="text-red-500">*</span>
                      </p>
                    </label>
                    <input
                      id="photo_right"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      class="hidden"
                      @change="handlePhotoSelect('right', $event)"
                    />
                    <p v-if="errors.photo_right" class="mt-1 text-xs text-red-600 dark:text-red-400">
                      {{ errors.photo_right }}
                    </p>
                  </div>

                </div>

                <!-- PREVIEW SECTION: 2 columns (only shown when photos selected) -->
                <div v-if="hasSelectedPhotos" class="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">

                  <!-- FRONT Preview -->
                  <div v-if="sidePhotos.front" class="border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">FRONT</p>
                    <img
                      :src="getPhotoPreview('front')!"
                      alt="Front preview"
                      class="w-full h-40 object-cover rounded-lg mb-3"
                    />
                    <button
                      type="button"
                      @click="removePhoto('front')"
                      class="w-full px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove
                    </button>
                  </div>

                  <!-- BACK Preview -->
                  <div v-if="sidePhotos.back" class="border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">BACK</p>
                    <img
                      :src="getPhotoPreview('back')!"
                      alt="Back preview"
                      class="w-full h-40 object-cover rounded-lg mb-3"
                    />
                    <button
                      type="button"
                      @click="removePhoto('back')"
                      class="w-full px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove
                    </button>
                  </div>

                  <!-- LEFT Preview -->
                  <div v-if="sidePhotos.left" class="border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">SIDE (L)</p>
                    <img
                      :src="getPhotoPreview('left')!"
                      alt="Left preview"
                      class="w-full h-40 object-cover rounded-lg mb-3"
                    />
                    <button
                      type="button"
                      @click="removePhoto('left')"
                      class="w-full px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove
                    </button>
                  </div>

                  <!-- RIGHT Preview -->
                  <div v-if="sidePhotos.right" class="border-2 border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">SIDE (R)</p>
                    <img
                      :src="getPhotoPreview('right')!"
                      alt="Right preview"
                      class="w-full h-40 object-cover rounded-lg mb-3"
                    />
                    <button
                      type="button"
                      @click="removePhoto('right')"
                      class="w-full px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove
                    </button>
                  </div>

                </div>
              </div>

              <!-- Footer -->
              <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  @click="handleClose"
                  :disabled="loading"
                  class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="loading"
                  class="px-4 py-2 text-white rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  :style="{ backgroundColor: brandColors.primary }"
                >
                  <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {{ loading ? 'Adding...' : 'Add Vehicle' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useBrand } from '@/composables/brand/useBrand';
import type { VehicleFormData } from '@/types/vehicle';

const props = defineProps<{
  isOpen: boolean;
  loading?: boolean;
}>();

const { brandColors } = useBrand();

const emit = defineEmits<{
  close: [];
  save: [data: VehicleFormData, photoFiles: File[], photoSides: string[]];
}>();

// Form data
const formData = ref<VehicleFormData>({
  vehicle_make: '',
  vehicle_model: '',
  vehicle_year: new Date().getFullYear(),
  plate_number: ''
});

// Vehicle photos - side-based structure
const sidePhotos = ref<Record<'front' | 'back' | 'left' | 'right', File | null>>({
  front: null,
  back: null,
  left: null,
  right: null
});

// Errors
const errors = ref<Record<string, string>>({});

// Year options (1900 to current year + 1)
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear + 1; year >= 1900; year--) {
    years.push(year);
  }
  return years;
});

// Handle photo selection for specific side
function handlePhotoSelect(side: 'front' | 'back' | 'left' | 'right', event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    const file = target.files[0];

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      errors.value[`photo_${side}`] = 'Only JPEG, PNG, and JPG files are allowed';
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      errors.value[`photo_${side}`] = 'File size must not exceed 5MB';
      return;
    }

    sidePhotos.value[side] = file;
    errors.value[`photo_${side}`] = '';
  }
}

// Remove photo for specific side
function removePhoto(side: 'front' | 'back' | 'left' | 'right') {
  sidePhotos.value[side] = null;
  errors.value[`photo_${side}`] = '';
}

// Get photo preview URL for specific side
function getPhotoPreview(side: 'front' | 'back' | 'left' | 'right'): string | null {
  const file = sidePhotos.value[side];
  return file ? URL.createObjectURL(file) : null;
}

// Check if any photos are selected
const hasSelectedPhotos = computed(() => {
  return Object.values(sidePhotos.value).some(photo => photo !== null);
});

function handleClose() {
  resetForm();
  emit('close');
}

function handleSubmit() {
  errors.value = {};

  // Validate that all 4 side photos are provided
  const sides = (['front', 'back', 'left', 'right'] as const);
  const sideLabels: Record<string, string> = {
    front: 'Front',
    back: 'Back',
    left: 'Side (Left)',
    right: 'Side (Right)',
  };
  let hasPhotoError = false;
  for (const side of sides) {
    if (!sidePhotos.value[side]) {
      errors.value[`photo_${side}`] = `${sideLabels[side]} photo is required`;
      hasPhotoError = true;
    }
  }
  if (hasPhotoError) return;

  // Build File array from non-null sides
  const photoFiles: File[] = [];
  const photoSides: string[] = [];

  Object.entries(sidePhotos.value).forEach(([side, file]) => {
    if (file) {
      photoFiles.push(file);
      photoSides.push(side);
    }
  });

  emit('save', { ...formData.value }, photoFiles, photoSides);
}

function resetForm() {
  formData.value = {
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: new Date().getFullYear(),
    plate_number: ''
  };
  sidePhotos.value = {
    front: null,
    back: null,
    left: null,
    right: null
  };
  errors.value = {};
}

// Expose resetForm so parent can call it
defineExpose({
  resetForm
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active > div > div,
.modal-leave-active > div > div {
  transition: transform 0.3s ease;
}

.modal-enter-from > div > div,
.modal-leave-to > div > div {
  transform: scale(0.95);
}
</style>
